'use client';

import { useEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { useAuth, type UseAuthResult } from '@/hooks/use-auth';
import { usePaymentReturn } from '@/tenants/_shared/hooks/usePaymentReturn';
import { trackRego } from '@/tenants/_shared/lib/registration-analytics';
import {
  resolveRegistrationInitState,
  type RegistrationInitState,
} from '../lib/registrationInitState';

export interface UseRegistrationInitResult {
  isLoading: boolean;
  initialStep: number;
  isRedirectReturn: boolean;
  /** User already has an active registration — show banner with dashboard link */
  isBlocked: boolean;
  pendingRegistration: RegistrationInitState['pendingRegistration'];
  authUser: UseAuthResult['user'];
  shouldSkipAuthStep: boolean;
  registrationStatus: UseAuthResult['registrationStatus'];
  paymentReturn: ReturnType<typeof usePaymentReturn>;
  authenticatedProfile: RegistrationInitState['authenticatedProfile'];
}

/**
 * Consolidates registration init logic into a single deterministic code path.
 *
 * Replaces the mount init useEffect and the resume/guard useEffect that
 * previously lived in RegisterPage, eliminating the Zustand hydration race
 * and the useEffect dependency maze.
 */
export function useRegistrationInit(
  initialCountryFromUrl: string,
  detectedCountry: string | null | undefined,
): UseRegistrationInitResult {
  const searchParams = useSearchParams();
  const {
    user: authUser,
    entrant,
    registrationStatus,
    isHydrating,
  } = useAuth({
    hydrateFromQuery: true,
  });
  const paymentReturn = usePaymentReturn();
  const isRedirectReturn = paymentReturn.isRedirectReturn;

  // Track rego_started once per mount
  const trackedRef = useRef(false);
  useEffect(() => {
    if (trackedRef.current || isRedirectReturn) return;
    trackedRef.current = true;
    trackRego('rego_started', {
      country_code: initialCountryFromUrl || detectedCountry || undefined,
      is_returning: !!authUser,
      attempt_number: 1,
      is_redirect_return: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resolved = resolveRegistrationInitState({
    isHydrating,
    authUser,
    entrant,
    registrationStatus,
    isRedirectReturn,
    urlIntent: searchParams.get('intent'),
  });

  if (resolved.pendingRegistration?.eventId && !resolved.isRedirectReturn && !resolved.isBlocked) {
    trackRego('rego_resume_from_existing', {
      intent: searchParams.get('intent') === 'changeRide' ? 'changeRide' : 'auto',
      has_pending_registration: true,
      has_checkout_state: !!resolved.pendingRegistration.checkoutState,
    });
  }

  return {
    ...resolved,
    authUser,
    registrationStatus,
    paymentReturn,
  };
}
